import { HeidiProFlashFilledSvg } from '@/assets/image/free-to-pay/heidi-pro-flash-filled-svg';
import ProductUsageDisclaimer from '@/components/common/product-usage-disclaimer';
import { ORGANISATION_ROLE_MESSAGE } from '@/components/modal/shared-signup.messages';
import { CQCPracticeAutocomplete } from '@/components/modal/steps/organisation-details-step/cqc-practice-autocomplete';
import { JurisdictionSelect } from '@/components/ui/jurisdiction-select/JurisdictionSelect';
import { PhoneInput } from '@/components/ui/phone-input';
import ClinicianTypeSelect from '@/components/ui/select/clinician-type-select';
import {
  OTHER_SPECIALTY_ID,
  useSelectedSpecialtyDisplayName,
} from '@/components/ui/select/clinician-type-select/useSelectedSpecialtyDisplayName';
import CountrySelect from '@/components/ui/select/country-select';
import { TERMS_OF_USE, USAGE_POLICY } from '@/components/utils/legalDocuments';
import {
  useIsCountrySpecificEhrSelectionEnabled,
  useIsWorkspaceOnboardingRenameEnabled,
} from '@/features/flags';
import { useIsAuth0OnboardingGateEnabled } from '@/features/flags/auth0-onboarding-gate-flag';
import {
  IS_UK_PRACTICE_SEARCH_ENABLED,
  IS_UK_PRACTICE_SEARCH_MANDATORY_ENABLED,
} from '@/features/flags/legacy-flags';
import { useAboutYouAnalytics } from '@/features/onboarding-v2/components/personalization/steps/hooks/useAboutYouAnalytics';
import { useOnboardingLocaleSync } from '@/features/onboarding-v2/hooks/useOnboardingLocaleSync';
import { useSelectedEmrOption } from '@/features/onboarding-v2/hooks/useSelectedEmrOption';
import { useQuerySpecialties } from '@/hooks/query/useQuerySpecialties';
import { useIsFreeUser } from '@/hooks/useIsFreeUser';
import { useWorkspaceNameDefault } from '@/hooks/useWorkspaceNameDefault';
import {
  countriesToRegion,
  isCountryInRegion,
  isEnterpriseRegion,
  isKnownRegion,
} from '@/lists/countriesToRegion';
import { enterpriseCountries } from '@/lists/enterpriseCountries';
import { useOnboardingFieldTracking } from '@/models/analytics/features/useOnboardingFieldTracking';
import { getPhoneNumberVariantForCountry } from '@/utils/country-config';
import {
  configureOnboardingProfileHost,
  type OnboardingProfileHost,
} from '@heidi/growth';
import {
  INTEGRATION_PROVIDER_OTHER_ID,
  useQueryIntegrationProviders,
} from '@heidi/integrations/ehr/data';

const buildOnboardingProfileHost = (): OnboardingProfileHost => ({
  getPhoneNumberVariantForCountry: (countryCode) =>
    getPhoneNumberVariantForCountry(countryCode),
  isUkPracticeSearchEnabled: () => IS_UK_PRACTICE_SEARCH_ENABLED(),
  isUkPracticeSearchMandatoryEnabled: () => IS_UK_PRACTICE_SEARCH_MANDATORY_ENABLED(),
  useIsCountrySpecificEhrSelectionEnabled: () =>
    useIsCountrySpecificEhrSelectionEnabled(),
  useIsWorkspaceOnboardingRenameEnabled: () => useIsWorkspaceOnboardingRenameEnabled(),
  useQueryIntegrationProviders: (args) => useQueryIntegrationProviders(args),
  integrationProviderOtherId: INTEGRATION_PROVIDER_OTHER_ID,
  useSelectedEmrOption: (emrEhr, countryCode) =>
    useSelectedEmrOption(emrEhr, countryCode),
  useOnboardingFieldTracking: (section) => useOnboardingFieldTracking(section),
  CQCPracticeAutocomplete,
  ClinicianTypeSelect,
  organisationRoleMessages: ORGANISATION_ROLE_MESSAGE,
  useAboutYouAnalytics: (params) => useAboutYouAnalytics(params),
  CountrySelect,
  JurisdictionSelect,
  isEnterpriseRegion: (region) => isEnterpriseRegion(region),
  enterpriseCountries,
  PhoneInput,
  HeidiProFlashFilledSvg,
  useIsFreeUser: () => useIsFreeUser(),
  useSelectedSpecialtyDisplayName: (selectedSpecialty, specialties) =>
    useSelectedSpecialtyDisplayName(selectedSpecialty, specialties),
  otherSpecialtyId: OTHER_SPECIALTY_ID,
  useQuerySpecialties: (select, options) => useQuerySpecialties(select, options),
  useIsAuth0OnboardingGateEnabled: () => useIsAuth0OnboardingGateEnabled(),
  useOnboardingLocaleSync: (language, options) =>
    useOnboardingLocaleSync(language, options),
  useWorkspaceNameDefault: () => useWorkspaceNameDefault(),
  ProductUsageDisclaimer,
  termsOfUseUrl: TERMS_OF_USE,
  usagePolicyUrl: USAGE_POLICY,
  // The Proxy itself: each index reads the regional override at call time.
  countriesToRegion,
  isCountryInRegion: (countryName, region) => isCountryInRegion(countryName, region),
  isKnownRegion: (region) => isKnownRegion(region),
});

configureOnboardingProfileHost(buildOnboardingProfileHost);
